import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2 } from "lucide-react";
import { ARTIFACT_TYPES } from "./artifactModel";
import { useMentionPrompt } from "./useMentionPrompt";
import { MentionPrompt } from "./MentionPrompt";

const TypeSelect = ({ value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm text-gray-300">Artifact Type</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#1a1a1a] border-white/10">
        {Object.entries(ARTIFACT_TYPES).map(([key, { name, icon: Icon }]) => (
          <SelectItem key={key} value={key} className="text-white hover:bg-white/10">
            <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-ism-teal" />{name}</div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const FormatButtons = ({ formats, value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm text-gray-300">Output Format</label>
    <div className="flex gap-2">
      {formats.map((format) => (
        <Button key={format} variant={value === format ? "default" : "outline"} size="sm" onClick={() => onChange(format)}
          className={value === format ? "bg-ism-teal hover:bg-ism-teal/90" : "border-white/20 text-gray-300 hover:bg-white/10"}>
          {format.toUpperCase()}
        </Button>
      ))}
    </div>
  </div>
);

const mentionId = (mentions, type) => mentions.find((m) => m.type === type)?.id;

export const GenerateArtifactModal = ({ open, onOpenChange, userId, onGenerated }) => {
  const [selectedType, setSelectedType] = useState("pitch_deck");
  const [selectedFormat, setSelectedFormat] = useState("pptx");
  const [generating, setGenerating] = useState(false);
  const m = useMentionPrompt(userId);
  const formats = useMemo(() => ARTIFACT_TYPES[selectedType]?.formats || ["pdf"], [selectedType]);

  useEffect(() => {
    if (!formats.includes(selectedFormat)) setSelectedFormat(formats[0]);
  }, [formats, selectedFormat]);

  const close = () => {
    onOpenChange(false);
    setSelectedType("pitch_deck");
    setSelectedFormat("pptx");
    m.reset();
  };

  const generate = async () => {
    if (!m.prompt.trim()) return toast.error("Please describe what you want to generate");
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/artifacts/generate/${userId}`, {
        artifactType: selectedType, outputFormat: selectedFormat, prompt: m.prompt,
        accountId: mentionId(m.mentions, "account"), opportunityId: mentionId(m.mentions, "opportunity"),
        prospectId: mentionId(m.mentions, "prospect"), persona: m.mentions.find((x) => x.type === "persona")?.name,
      });
      if (response.data.success) {
        toast.success(`${ARTIFACT_TYPES[selectedType].name} generated successfully!`);
        onGenerated(response.data.artifact);
        close();
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate artifact");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-[#111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-ism-teal" />Generate Artifact</DialogTitle>
          <DialogDescription className="text-gray-400">AI will create deliverables using your brand guidelines</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <TypeSelect value={selectedType} onChange={setSelectedType} />
          <FormatButtons formats={formats} value={selectedFormat} onChange={setSelectedFormat} />
          <MentionPrompt m={m} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} className="border-white/20 text-gray-300 hover:bg-white/10">Cancel</Button>
          <Button onClick={generate} disabled={generating || !m.prompt.trim()} className="bg-ism-teal hover:bg-ism-teal/90" data-testid="generate-artifact-submit">
            {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate {selectedFormat.toUpperCase()}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
